import type {
  AdminUserTransactionsResponse,
  AuditLogEntry,
  BanUserResponse,
  CurrencyAdjustRequest,
  CurrencyAdjustResponse,
  UnbanUserResponse,
  UserDetailResponse,
  UserSearchResponse,
} from '@shared/types';
import type { AdminUserWindow } from '@shared/constants';
import { api } from './api';

/**
 * Admin users API client. Wraps the REST endpoints in
 * `server/src/routes/admin.ts` introduced in epic #281 (see #285 for users).
 *
 * Server support matrix:
 *   - GET  /admin/users?q=&page=&limit=&online=&banned=&bots=&since=&active=
 *                                         → paginated search + dashboard filters
 *   - GET  /admin/users/:id               → detail + last 10 matches
 *   - POST /admin/users/:id/ban           → body `{ reason? }`, returns `{ id, isBanned: true }`
 *   - POST /admin/users/:id/unban         → no body, returns `{ id, isBanned: false }`
 *   - POST /admin/users/:id/currency      → body `{ coinsDelta?, gemsDelta?, energyDelta?, reason }`,
 *                                           returns final balances `{ id, coins, gems, energy, maxEnergy }`
 *   - GET  /admin/audit?targetType=user&targetId=:id&pageSize=5  → short recent audit preview
 *
 * The audit endpoint is reused directly here rather than exposing a separate
 * `services/audit.ts` — the user detail screen only needs the targetId-filtered
 * preview, not a generic audit client. When #288 lands an audit viewer screen
 * this can be promoted to a dedicated module.
 */

export interface ListUsersParams {
  q?: string;
  page?: number;
  limit?: number;
  /** Only users holding a live presence key (dashboard drill-down). */
  online?: boolean;
  banned?: boolean;
  /** `true` = bots only, `false` = exclude bots, omitted = both. */
  bots?: boolean;
  /** Signup window: accounts created within the last 24h / 3d / 7d / 30d. */
  since?: AdminUserWindow;
  /** Activity window: accounts whose `lastActiveAt` falls within it. */
  active?: AdminUserWindow;
}

/** Shape of the `/admin/audit` response. Matches controller's `res.json`. */
export interface AuditListResponse {
  data: AuditLogEntry[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

/** Build a query string, skipping empty / undefined values. */
function toQuery(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export async function listUsers(
  params: ListUsersParams = {},
): Promise<UserSearchResponse> {
  const qs = toQuery({
    q: params.q,
    page: params.page ?? 1,
    limit: params.limit ?? 20,
    // The server takes the literal string 'true' for these (a stray '1' must
    // not silently filter the list), so only send the key when it is on.
    online: params.online ? 'true' : undefined,
    banned: params.banned ? 'true' : undefined,
    bots: params.bots === undefined ? undefined : String(params.bots),
    since: params.since,
    active: params.active,
  });
  return api<UserSearchResponse>(`/admin/users${qs}`);
}

export async function getUser(id: string): Promise<UserDetailResponse> {
  return api<UserDetailResponse>(`/admin/users/${encodeURIComponent(id)}`);
}

/**
 * Ban a user. Reason is REQUIRED (3..500 chars) server-side — banning is the
 * most destructive admin action (revokes all tokens, disables account), so
 * every call must carry an audit-grade rationale.
 */
export async function banUser(
  id: string,
  reason: string,
): Promise<BanUserResponse> {
  return api<BanUserResponse>(
    `/admin/users/${encodeURIComponent(id)}/ban`,
    { method: 'POST', body: JSON.stringify({ reason: reason.trim() }) },
  );
}

/**
 * Unban a user. Reason is REQUIRED — same compliance bar as ban, because
 * restoring access for a disabled account is nearly as consequential.
 */
export async function unbanUser(
  id: string,
  reason: string,
): Promise<UnbanUserResponse> {
  return api<UnbanUserResponse>(
    `/admin/users/${encodeURIComponent(id)}/unban`,
    { method: 'POST', body: JSON.stringify({ reason: reason.trim() }) },
  );
}

export async function adjustCurrency(
  id: string,
  req: CurrencyAdjustRequest,
): Promise<CurrencyAdjustResponse> {
  return api<CurrencyAdjustResponse>(
    `/admin/users/${encodeURIComponent(id)}/currency`,
    { method: 'POST', body: JSON.stringify(req) },
  );
}

/**
 * Fetch a short audit-log preview scoped to this user. Uses the shared
 * `/admin/audit` endpoint with `targetType=user&targetId=:id`. Default
 * pageSize=5 matches the detail-screen preview requirement from #290.
 */
export async function getUserAudit(
  id: string,
  pageSize = 5,
): Promise<AuditListResponse> {
  const qs = toQuery({
    targetType: 'user',
    targetId: id,
    page: 1,
    pageSize,
  });
  return api<AuditListResponse>(`/admin/audit${qs}`);
}

/**
 * Paginated transaction history for a user. Newest entry first. Metadata is
 * passed through as-is — the logging service already structures it per
 * transaction source (match reward, power-up spend, IAP, ad reward).
 */
export async function listUserTransactions(
  id: string,
  params: { page?: number; limit?: number } = {},
): Promise<AdminUserTransactionsResponse> {
  const qs = toQuery({
    page: params.page ?? 1,
    limit: params.limit ?? 50,
  });
  return api<AdminUserTransactionsResponse>(
    `/admin/users/${encodeURIComponent(id)}/transactions${qs}`,
  );
}
